import { supabase } from '../supabase';
import { uploadMarketCommentAttachment } from './market_comments';
import type { Shipment, MarketComment } from '../utils/types';

export type ShipmentLogbookScope = Pick<
  Shipment,
  'id' | 'exporter_id' | 'season' | 'species_id'
>;
export type ShipmentLogbookComment = MarketComment & {
  shipment_id: number;
  logbook_kind: 'market' | 'note';
  capture_id: string | null;
  editor?: {
    id: number;
    first_name: string | null;
    last_name: string | null;
  } | null;
};
export interface ShipmentLogbookAccess {
  can_read_notes: boolean;
}
export interface ShipmentLogbookSummary {
  last_activity_at: string | null;
  last_actor: string | null;
}
export interface ShipmentLogbookData {
  state: ShipmentLogbookAccess;
  comments: ShipmentLogbookComment[];
  photoUrls: Record<string, string | null>;
}
const args = (s: ShipmentLogbookScope) => ({
  p_id: s.id,
  p_exporter: s.exporter_id,
  p_season: s.season,
  p_species: s.species_id,
});

export async function getShipmentLogbook(
  s: ShipmentLogbookScope
): Promise<ShipmentLogbookData> {
  const { data: state, error } = await supabase.rpc(
    'get_shipment_logbook_access',
    args(s)
  );
  if (error) throw error;
  if (!state || typeof state.can_read_notes !== 'boolean')
    throw new Error('Invalid logbook response');
  const comments: ShipmentLogbookComment[] = [];
  if (state.can_read_notes) {
    let before: number | null = null;
    for (;;) {
      let q = supabase
        .from('market_comments')
        .select(
          '*,created_by:users!market_comments_created_by_fkey(id,first_name,last_name),editor:users!market_comments_edited_by_fkey(id,first_name,last_name),attachments:market_comment_attachments(*)'
        )
        .eq('shipment_id', s.id)
        .eq('exporter_id', s.exporter_id)
        .eq('season', s.season)
        .eq('species_id', s.species_id)
        .is('deleted_at', null)
        .order('id', { ascending: false })
        .limit(100);
      if (before !== null) q = q.lt('id', before);
      const { data, error: e } = await q;
      if (e) throw e;
      const page = (data || []) as unknown as ShipmentLogbookComment[];
      comments.push(...page);
      if (page.length < 100) break;
      const next = Number(page[page.length - 1].id);
      if (
        !Number.isSafeInteger(next) ||
        next <= 0 ||
        (before !== null && next >= before)
      )
        throw new Error('Invalid logbook cursor');
      before = next;
    }
  }
  const photoUrls: Record<string, string | null> = {};
  const buckets = new Map<string, Set<string>>();
  for (const comment of comments)
    for (const photo of comment.attachments || []) {
      if (photo.deleted_at || !photo.is_image) continue;
      const paths = buckets.get(photo.bucket) || new Set<string>();
      paths.add(photo.path);
      buckets.set(photo.bucket, paths);
    }
  for (const [bucket, paths] of buckets) {
    const list = [...paths];
    for (let offset = 0; offset < list.length; offset += 100) {
      const chunk = list.slice(offset, offset + 100);
      const { data, error: e } = await supabase.storage
        .from(bucket)
        .createSignedUrls(chunk, 3600);
      for (const path of chunk) photoUrls[`${bucket}/${path}`] = null;
      if (!e)
        for (const row of data || [])
          if (row.path && row.signedUrl)
            photoUrls[`${bucket}/${row.path}`] = row.signedUrl;
    }
  }
  return { state: state as ShipmentLogbookAccess, comments, photoUrls };
}

export async function addShipmentLogbookNote(
  s: ShipmentLogbookScope,
  content: string,
  date: string,
  requestId: string,
  hasPhotos: boolean
): Promise<number> {
  const { data, error } = await supabase.rpc('add_shipment_logbook_note', {
    ...args(s),
    p_content: content,
    p_date: date,
    p_request: requestId,
    p_has_photos: hasPhotos,
  });
  if (error) throw error;
  if (!Number.isSafeInteger(Number(data)) || Number(data) <= 0)
    throw new Error('Invalid note ID');
  return Number(data);
}
export async function getShipmentLogbookSummaries(
  exporterId: number,
  season: string,
  speciesId: number,
  ids: number[]
): Promise<Record<string, ShipmentLogbookSummary>> {
  const unique = [...new Set(ids)],
    result: Record<string, ShipmentLogbookSummary> = {};
  for (let offset = 0; offset < unique.length; offset += 200) {
    const { data, error } = await supabase.rpc(
      'get_shipment_logbook_summaries',
      {
        p_exporter: exporterId,
        p_season: season,
        p_species: speciesId,
        p_ids: unique.slice(offset, offset + 200),
      }
    );
    if (error) throw error;
    if (!data || typeof data !== 'object' || Array.isArray(data))
      throw new Error('Invalid logbook summaries');
    Object.assign(result, data);
  }
  return result;
}
export async function uploadShipmentNotePhoto(
  commentId: number,
  exporterId: number,
  userId: number,
  file: File,
  requestKey: string
) {
  return uploadMarketCommentAttachment({
    commentId,
    exporterId,
    created_by: userId,
    file,
    requestKey,
  });
}
